import React, { useMemo } from "react";
import { Button, Popconfirm, Space, Table, Tag } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { ManagedAdmin, ManagedDomain, ManagedUser } from "../../../types";
import type { ListItem, ModalMode, TabKey } from "../constants";
import { formatDate } from "../utils";

interface MainTableProps {
  tab: TabKey;
  items: ListItem[];
  loading: boolean;
  onEdit: (id: string, type: ModalMode) => void;
  onDelete: (type: ModalMode, id: string) => void;
}

export const MainTable: React.FC<MainTableProps> = ({ tab, items, loading, onEdit, onDelete }) => {
  const cols = useMemo<ColumnsType<ListItem>>(() => {
    const actions = (type: ModalMode, id: string) => (
      <Space>
        <Button icon={<EditOutlined />} onClick={() => onEdit(id, type)}>Edit</Button>
        <Popconfirm title="Move to trash?" onConfirm={() => onDelete(type, id)}>
          <Button danger icon={<DeleteOutlined />}>Delete</Button>
        </Popconfirm>
      </Space>
    );

    if (tab === "domains") {
      return [
        { title: "Code", dataIndex: "code", key: "code" },
        { title: "Name", dataIndex: "name", key: "name" },
        { title: "URL", dataIndex: "url", key: "url", render: (v) => v || "-" },
        {
          title: "Status",
          key: "status",
          render: (_, record) =>
            (record as ManagedDomain).isActive === false
              ? <Tag color="red">Inactive</Tag>
              : <Tag color="green">Active</Tag>,
        },
        {
          title: "Actions",
          key: "actions",
          fixed: "right",
          width: 150,
          render: (_, record) => actions("domains", record.id),
        },
      ];
    }

    if (tab === "roles") {
      return [
        { title: "Name", dataIndex: "name", key: "name" },
        { title: "Level", dataIndex: "level", key: "level" },
        { title: "Updated", key: "updatedAt", render: (_, r) => formatDate(r.updatedAt || r.createdAt) },
        {
          title: "Actions",
          key: "actions",
          fixed: "right",
          width: 150,
          render: (_, record) => actions("roles", record.id),
        },
      ];
    }

    return [
      { title: "Name", dataIndex: "name", key: "name" },
      { title: "Login", dataIndex: "login", key: "login" },
      { title: "Email", dataIndex: "email", key: "email", render: (v) => v || "-" },
      ...(tab === "admins"
        ? [{
            title: "Backend Role",
            key: "backendRole",
            render: (_: unknown, record: ListItem) => (record as ManagedAdmin).backendRole?.name || "-",
          }]
        : []),
      {
        title: "Domains",
        key: "domains",
        render: (_, record) => {
          const r = record as ManagedUser | ManagedAdmin;
          if (!r.domains?.length) return "-";
          return (
            <Space wrap>
              {r.domains.slice(0, 3).map((domain) => (
                <Tag key={domain.id || domain.code} color="blue">{domain.name}</Tag>
              ))}
            </Space>
          );
        },
      },
      { title: "Updated", key: "updatedAt", render: (_, r) => formatDate(r.updatedAt || r.createdAt) },
      {
        title: "Actions",
        key: "actions",
        fixed: "right",
        width: 150,
        render: (_, record) => actions(tab as ModalMode, record.id),
      },
    ];
  }, [tab, onEdit, onDelete]);

  return <Table rowKey="id" columns={cols} dataSource={items} loading={loading} pagination={false} bordered scroll={{ x: "max-content" }} />;
};
